import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Space,
  Alert,
  message,
  Tooltip,
  Divider
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { apiService } from '../../services/api';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

interface ProjectSpec {
  project_name: string;
  media_types: string[];
  content_categories: string[];
  // Global study fields
  governance_safety?: string;
  persona_session?: string;
  business_domain?: string;
  analytical_methods?: string;
  temporal_freshness?: string;
}

interface MediaType {
  media_type_id: string;
  name: string;
  label: string;
  description?: string;
  file_extensions?: string[];
  display_order: string;
}

interface ContentCategory {
  category_id: string;
  name: string;
  label: string;
  description?: string;
  display_order: string;
}

// Global study field configurations with tooltips
const GLOBAL_STUDY_FIELDS = [
  {
    name: 'governance_safety',
    label: 'Governance & Safety',
    tooltip: 'Privacy rules, regulatory constraints (GDPR, HIPAA), red-/green-lists, tone/brand style, disallowed content',
    type: 'textarea',
    rows: 4
  },
  {
    name: 'persona_session',
    label: 'Persona & Session',
    tooltip: 'User role (analyst, exec), language preference, familiarity level, recent chat history, open tickets/tasks',
    type: 'textarea',
    rows: 4
  },
  {
    name: 'business_domain',
    label: 'Business Domain',
    tooltip: 'Mission, products, strategic pillars, industry definitions, normative trends, past reports',
    type: 'textarea',
    rows: 4
  },
  {
    name: 'analytical_methods',
    label: 'Analytical Methods',
    tooltip: 'Approved statistical tests, ML model catalogue, deterministic formulas, causal frameworks',
    type: 'textarea',
    rows: 4
  },
  {
    name: 'temporal_freshness',
    label: 'Temporal & Freshness',
    tooltip: 'Data refresh intervals, release calendars, embargo windows',
    type: 'textarea',
    rows: 3
  }
];

const StudyBuilder: React.FC = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mediaTypes, setMediaTypes] = useState<MediaType[]>([]);
  const [contentCategories, setContentCategories] = useState<ContentCategory[]>([]);
  const [configLoading, setConfigLoading] = useState(true);

  useEffect(() => {
    loadConfiguration();
    if (studyId) {
      loadProject(studyId);
    }
  }, [studyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadConfiguration = async () => {
    setConfigLoading(true);
    try {
      const [mediaTypesData, contentCategoriesData] = await Promise.all([
        apiService.getMediaTypes(),
        apiService.getContentCategories()
      ]);
      
      setMediaTypes(mediaTypesData);
      setContentCategories(contentCategoriesData);
    } catch (error) {
      console.error('Failed to load configuration:', error);
      message.error('Failed to load media types and categories');
    } finally {
      setConfigLoading(false);
    }
  };

  const loadProject = async (id: string) => {
    try {
      const project = await apiService.getStudy(id);
      form.setFieldsValue(project);
    } catch (error) {
      console.error('Failed to load project:', error);
      message.error('Failed to load project');
    }
  };

  const handleProjectSave = async (values: ProjectSpec) => {
    setLoading(true);
    try {
      // Format the data for the API
      const projectData = {
        project_name: values.project_name,
        media_types: values.media_types || [],
        content_categories: values.content_categories || [],
        governance_safety: values.governance_safety || '',
        persona_session: values.persona_session || '',
        business_domain: values.business_domain || '',
        analytical_methods: values.analytical_methods || '',
        temporal_freshness: values.temporal_freshness || ''
      };

      if (studyId) {
        // Update existing project (TODO: implement update endpoint)
        message.info('Update functionality not yet implemented');
        return;
      } else {
        // Create new project
        await apiService.createStudy(projectData);
        message.success('Study created successfully');
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Failed to save project:', error);
      message.error(error.message || 'Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  const renderFieldWithTooltip = (field: any) => {
    const tooltip = (
      <div style={{ maxWidth: '300px' }}>
        {field.tooltip}
      </div>
    );

    const inputComponent = field.type === 'textarea' ? (
      <TextArea
        rows={field.rows}
        placeholder={`Enter ${field.label.toLowerCase()} information...`}
      />
    ) : (
      <Input placeholder={`Enter ${field.label.toLowerCase()}...`} />
    );

    return (
      <Form.Item
        name={field.name}
        label={
          <Space>
            {field.label}
            <Tooltip title={tooltip}>
              <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'help' }} />
            </Tooltip>
          </Space>
        }
      >
        {inputComponent}
      </Form.Item>
    );
  };

  return (
    <div style={{ position: 'relative', minHeight: '500px' }}>
      {configLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          flexDirection: 'column',
          borderRadius: '8px'
        }}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: 500, 
              marginBottom: '12px',
              color: '#1890ff'
            }}>
              Loading Configuration...
            </div>
            <div style={{ 
              fontSize: '14px', 
              color: '#666',
              marginBottom: '16px'
            }}>
              Fetching available media types and content categories
            </div>
            <div style={{
              width: '32px',
              height: '32px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #1890ff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto'
            }}></div>
          </div>
        </div>
      )}
      
      <div style={{ marginBottom: 24, opacity: configLoading ? 0.5 : 1 }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/dashboard')}
            disabled={configLoading}
          >
            Back to Dashboard
          </Button>
        </Space>
        <Title level={2} style={{ marginTop: 16 }}>
          {studyId ? 'Edit Study Configuration' : 'New Study Configuration'}
        </Title>
        <Paragraph>
          Configure your study parameters and AI analysis settings. After creating the study, you can add research questions and manage media files separately.
        </Paragraph>
      </div>

      <Card 
        title="Study Configuration"
        style={{ opacity: configLoading ? 0.5 : 1 }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleProjectSave}
          style={{ pointerEvents: configLoading ? 'none' : 'auto' }}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item
                name="project_name"
                label="Project Name"
                rules={[{ required: true, message: 'Please enter project name' }]}
              >
                <Input placeholder="e.g., AIBM Fatherhood Study" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="media_types"
                label="Media Types"
                rules={[{ required: true, message: 'Please select or enter media types' }]}
              >
                <Select
                  mode="tags"
                  placeholder="Select types or enter custom ones"
                  tokenSeparators={[',']}
                  loading={configLoading}
                >
                  {mediaTypes.map(type => (
                    <Select.Option key={type.name} value={type.name} title={type.description}>
                      {type.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                name="content_categories"
                label="Content Categories"
                rules={[{ required: true, message: 'Please select or enter content categories' }]}
              >
                <Select
                  mode="tags"
                  placeholder="Select categories or enter custom ones"
                  tokenSeparators={[',']}
                  loading={configLoading}
                >
                  {contentCategories.map(category => (
                    <Select.Option key={category.name} value={category.name} title={category.description}>
                      {category.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Alert
            message="AI Global Configuration"
            description="Configure AI analysis settings that will apply to all sessions in this study"
            type="info"
            style={{ marginBottom: 16 }}
          />

          <Row gutter={[16, 16]}>
            {GLOBAL_STUDY_FIELDS.map((field, index) => (
              <Col span={24} key={index}>
                {renderFieldWithTooltip(field)}
              </Col>
            ))}
          </Row>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<SaveOutlined />}
              >
                {studyId ? 'Update Study Configuration' : 'Create Study'}
              </Button>
              <Button onClick={() => navigate('/dashboard')}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default StudyBuilder; 